import { useQuery, useQueryClient } from '@tanstack/react-query'
import { listScheduleEvents } from '@sportninja/common/services/event/listScheduleEvents'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { SnEvent } from '../types'
import {
  buildCompetitionEventsQuery,
  type BuildCompetitionEventsQueryArgs,
} from '../buildCompetitionEventsQuery'

export interface UseCompetitionEventsArgs
  extends BuildCompetitionEventsQueryArgs {
  /**
   * The schedule to list: the selected division, else the selected season,
   * else the competition the page is for. The caller resolves that precedence
   * — it already tracks it to point the games list at the same schedule, and
   * duplicating it here would let the two lists disagree.
   */
  scheduleId?: string | null
  /** False while the schedule filters are still resolving; keeps it idle. */
  enabled?: boolean
}

export interface UseCompetitionEventsReturn {
  events: SnEvent[]
  isLoading: boolean
  isError: boolean
  /** Refetches when the list has gone stale; a no-op inside the window. */
  refetch: () => void
}

/**
 * Matches `useScheduleEvents` and `useMyScheduleEvents`. Events change often
 * enough that a longer window shows stale rows after someone adds one.
 */
const EVENT_LIST_STALE_TIME = 1000 * 15

const EMPTY: SnEvent[] = []

/**
 * Events for a competition's schedule list, scoped by the page's own filters —
 * season, division, team, venue and surface.
 *
 * The counterpart of `useScheduleEvents`, which answers for a TEAM. The
 * competition page has no team of its own, so it cannot use that hook: it
 * would have no id to scope by and would return nothing at all. The two are
 * kept separate rather than merged because their scoping rules genuinely
 * differ — see the note on the team filter in `buildCompetitionEventsQuery`.
 *
 * Fetches through `/schedules/{id}/events`, which answers for the schedule
 * itself rather than the viewer's memberships — so a league admin or anyone
 * browsing a competition they are not rostered in sees its events too.
 */
export const useCompetitionEvents = ({
  scheduleId,
  enabled = true,
  ...args
}: UseCompetitionEventsArgs): UseCompetitionEventsReturn => {
  'use memo'

  const queryClient = useQueryClient()

  const query = buildCompetitionEventsQuery(args)
  const queryKey = queryKeys.events.bySchedule(scheduleId, query)

  // The schedule is the request PATH, so without one there is nothing to ask.
  const isEnabled = enabled && Boolean(scheduleId)

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => listScheduleEvents(scheduleId as string, query),
    enabled: isEnabled,
    staleTime: EVENT_LIST_STALE_TIME,
    // Overrides the app-wide `refetchOnWindowFocus: false`: the period tabs
    // re-filter the fetched list client-side rather than re-querying, so
    // returning to the tab is the only moment an edit made elsewhere lands.
    refetchOnWindowFocus: true,
  })

  // Not `refetch()` and not `invalidateQueries` — both bypass staleTime and hit
  // the network on every call, so flipping period tabs would fire a request per
  // click. This refetches only once the window has lapsed.
  const refetch = () => {
    if (!isEnabled) {
      return
    }
    queryClient.refetchQueries({ queryKey, type: 'active', stale: true })
  }

  return {
    events: data ?? EMPTY,
    // A disabled query reports `isLoading: false`. While the filters are still
    // resolving the caller IS waiting, so report loading rather than letting
    // the list settle empty and flash an empty state.
    isLoading: enabled ? isLoading : true,
    isError,
    refetch,
  }
}
